import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Book, TrendingUp, BarChart, Info,
    Users, Package,
    BookOpen, BookText, Scale, FileSearch, NotebookPen,
    FileText, Warehouse, FileClock, Receipt, ChartColumnBig,
    PackageCheck
} from 'lucide-react';
import Card from '../components/ui/Card';
import { useToast } from '../hooks/useToast';
// import AccountPostingsReport from './AccountPostingsReport';
// import GeneralJournalReport from './GeneralJournalReport';
// import TrialBalanceReport from './TrialBalanceReport';
// import GeneralLedgerReport from './GeneralLedgerReport';
// import CustomerList from './CustomerList';
// import PNLReport from './PNLReport';
import { useNavigate } from 'react-router-dom';

/**
 * Helper function to process raw accounting data into specific report formats,
 * filtered by a given date range.
 * This centralizes the data transformation logic for better readability and maintainability.
 * @param {Array} rawData - The raw accounts data with nested postings from the API.
 * @param {Date|null} startDate - The start date for filtering transactions.
 * @param {Date|null} endDate - The end date for filtering transactions.
 * @returns {object} An object containing the processed data for each report type.
 */
const processReportsData = (rawData, startDate, endDate) => {
    const generalLedgerData = [];
    const trialBalanceMap = new Map();
    const expenseReportData = [];

    // Filter accounts and their postings by the date range before processing.
    const filteredRawData = rawData.map(account => ({
        ...account,
        postings: account.postings.filter(posting => {
            const transactionDate = new Date(posting.transaction?.transaction_date);
            const isAfterStart = !startDate || transactionDate >= startDate;
            const isBeforeEnd = !endDate || transactionDate <= endDate;
            return isAfterStart && isBeforeEnd;
        })
    }));

    // Iterate through each filtered account to build the report datasets.
    filteredRawData.forEach(account => {
        let accountDebitTotal = 0;
        let accountCreditTotal = 0;

        // Process each posting within the current account.
        account.postings.forEach(posting => {
            const transaction = posting.transaction || {};
            const currencySymbol = transaction.currency?.setcnote || '';
            const amount = parseFloat(posting.amount);

            // 1. Build the General Ledger data
            generalLedgerData.push({
                id: posting.posting_id,
                accountId: account.account_id,
                accountName: account.name,
                accountCode: account.account_code,
                transactionDate: transaction.transaction_date,
                transactionRef: transaction.ref,
                transactionType: transaction.transaction_type,
                description: transaction.description,
                amount: amount,
                postingType: posting.posting_type,
                creatorName: `${transaction.creator?.user?.first_name || ''} ${transaction.creator?.user?.last_name || ''}`.trim() || 'N/A',
                customerName: `${transaction.customer?.user?.first_name || ''} ${transaction.customer?.user?.last_name || ''}`.trim() || 'N/A',
                currency: transaction.currency?.setccurr || 'N/A',
                currencySymbol: currencySymbol,
            });

            // 2. Aggregate totals for the Trial Balance
            if (posting.posting_type === 'debit') {
                accountDebitTotal += amount;
            } else {
                accountCreditTotal += amount;
            }

            // 3. Build the Expense Report data
            if (account.account_type?.code === 'expense' && posting.posting_type === 'debit') {
                expenseReportData.push({
                    id: posting.posting_id,
                    transactionDate: transaction.transaction_date,
                    transactionRef: transaction.ref,
                    expenseAccount: account.name,
                    amount: amount,
                    currency: transaction.currency?.setccurr || 'N/A',
                    currencySymbol: currencySymbol,
                    description: transaction.description,
                    creatorName: `${transaction.creator?.user?.first_name || ''} ${transaction.creator?.user?.last_name || ''}`.trim() || 'N/A',
                });
            }
        });

        // 4. Update/Add entry for the Trial Balance.
        const isDebitBalanceAccount = ['asset', 'expense', 'fixed_asset', 'accounts_receivable', 'other_current_asset', 'office_supplies', 'advertising', 'utilities', 'cost_of_goods_sold', 'other_expense'].includes(account.account_type?.code);
        const currentBalance = isDebitBalanceAccount ? accountDebitTotal - accountCreditTotal : accountCreditTotal - accountDebitTotal;

        // Only include accounts with activity in the Trial Balance.
        if (accountDebitTotal !== 0 || accountCreditTotal !== 0) {
            trialBalanceMap.set(account.account_id, {
                accountId: account.account_id,
                accountCode: account.account_code,
                accountName: account.name,
                accountType: account.account_type?.label || 'N/A',
                debitTotal: accountDebitTotal,
                creditTotal: accountCreditTotal,
                balance: currentBalance,
                balanceType: isDebitBalanceAccount ? 'Debit' : 'Credit',
                currency: account.postings?.[0]?.transaction?.currency?.setccurr || 'N/A',
                currencySymbol: account.postings?.[0]?.transaction?.currency?.setcnote || '',
                postings: account.postings,
            });
        }
    });

    // Sort the final datasets for consistent presentation.
    const sortedGeneralLedger = generalLedgerData.sort((a, b) => new Date(b.transactionDate) - new Date(a.transactionDate));
    const sortedTrialBalance = Array.from(trialBalanceMap.values()).sort((a, b) => a.accountCode.localeCompare(b.accountCode));
    const sortedExpenseReport = expenseReportData.sort((a, b) => new Date(b.transactionDate) - new Date(a.transactionDate));

    return {
        generalLedgerData: sortedGeneralLedger,
        trialBalanceData: sortedTrialBalance,
        expenseReportData: sortedExpenseReport,
    };
};

/**
 * AccountingReports Component
 * Displays various accounting reports based on fetched Chart of Accounts data with postings.
 */
const AccountingReports = () => {
    const { showToast } = useToast();
    const navigate = useNavigate();

    const [user, setUser] = useState(null);
    const [allAccountsData, setAllAccountsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('generalLedger');
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [activeReport, setActiveReport] = useState(null);

    // Default dates to a two-day span: yesterday to today.
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const [startDate, setStartDate] = useState(yesterday);
    const [endDate, setEndDate] = useState(today);

    const [stores, setStores] = useState([]);
    const [selectedStoreId, setSelectedStoreId] = useState(null);
    const [salesData, setSalesData] = useState([]);
    const [salesLoading, setSalesLoading] = useState(false);
    const [salesError, setSalesError] = useState(null);

    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (!lcUser) {
            showToast("Please log in to view accounting reports.", "info");
            navigate('/login');
        } else {
            setUser(JSON.parse(lcUser));
        }
    }, [showToast, navigate]);

    if (!user) {
        return (
            <div className='flex flex-col items-center justify-center min-h-[60vh] text-center p-8 text-orange-600'>
                <Info size={32} className="mb-2" />
                <p className="text-lg mb-4">Please log in to view accounting reports.</p>
            </div>
        );
    }

    // if (selectedAccount) {
    //     return <AccountPostingsReport account={selectedAccount} onBack={() => setSelectedAccount(null)} />;
    // }

    // if (activeReport === 'generalJournal') {
    //     return <GeneralJournalReport onBack={() => setActiveReport(null)} />;
    // }
    // if (activeReport === 'trialBalance') {
    //     return <TrialBalanceReport onBack={() => setActiveReport(null)} />;
    // }
    // if (activeReport === 'generalLedger') {
    //     return <GeneralLedgerReport onBack={() => setActiveReport(null)} />;
    // }
    // if (activeReport === 'customerList') {
    //     return <CustomerList onBack={() => setActiveReport(null)} />;
    // }
    // if (activeReport === 'profitLoss') {
    //     return <PNLReport onBack={() => setActiveReport(null)} />;
    // }

    return (
        <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen font-sans">
            <Card className="p-4 sm:p-6 shadow-xl rounded-lg bg-white">
                <div className="p-8 bg-gray-50 rounded-lg flex flex-col items-center justify-center text-center">
                    <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2 mb-2">
                        <Book size={28} className="text-purple-600" /> Accounting Reports Dashboard
                    </h1>
                    <p className="text-gray-600 mb-8 max-w-xl">
                        Select a report below to get a detailed view of your financial data,
                        with options to filter by date range and other criteria.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 w-full max-w-4xl">
                        <button
                            type="button"
                            className="font-semibold rounded-lg transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed bg-gray-100 text-gray-900 hover:bg-gray-200 focus:ring-gray-200 px-4 py-4 text-base w-auto flex flex-col items-center justify-center p-6 min-h-[150px] text-lg font-semibold shadow-md hover:shadow-lg transition-all duration-200"
                            onClick={() => navigate('general-ledger-report')}
                        >
                            <BookText size={48} className="mb-3 text-blue-500" aria-hidden="true" />
                            General Ledger Report
                            <span className="text-sm font-normal text-gray-600 mt-2">
                                Transactions in each account for the selected period.
                            </span>
                        </button>
                        <button
                            type="button"
                            className="font-semibold rounded-lg transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed bg-gray-100 text-gray-900 hover:bg-gray-200 focus:ring-gray-200 px-4 py-4 text-base w-auto flex flex-col items-center justify-center p-6 min-h-[150px] text-lg font-semibold shadow-md hover:shadow-lg transition-all duration-200"
                            onClick={() => navigate('account-ledger-report')}
                        >
                            <FileSearch size={48} className="mb-3 text-cyan-600" aria-hidden="true" />
                            Account Ledger Report
                            <span className="text-sm font-normal text-gray-600 mt-2 text-center">
                                Detailed transaction history for a specific individual account.
                            </span>
                        </button>
                        <button
                            type="button"
                            className="font-semibold rounded-lg transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed bg-gray-100 text-gray-900 hover:bg-gray-200 focus:ring-gray-200 px-4 py-4 text-base w-auto flex flex-col items-center justify-center p-6 min-h-[150px] text-lg font-semibold shadow-md hover:shadow-lg transition-all duration-200"
                            onClick={() => navigate('general-journal-report')}
                        >
                            <NotebookPen size={48} className="mb-3 text-orange-500" aria-hidden="true" />
                            General Journal Report
                            <span className="text-sm font-normal text-gray-600 mt-2">
                                Posted transactions in journal format (showing debits and credits).
                            </span>
                        </button>
                        <button
                            type="button"
                            className="flex flex-col items-center justify-center p-6 min-h-[150px] w-auto rounded-lg bg-gray-100 text-gray-900 font-semibold shadow-md transition-all duration-200 ease-in-out hover:bg-gray-200 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-gray-200 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={() => navigate('/reports/vendor-ledger-report')}
                        >
                            {/* Icon: Swapped to Users for Vendor context, updated to emerald/green for financial subledgers */}
                            <Users size={48} className="mb-3 text-emerald-600" aria-hidden="true" />

                            <span className="text-lg">Vendor Subledger Report</span>

                            <span className="mt-2 text-sm font-normal text-gray-600">
                                Detailed transaction history and balances for individual vendors.
                            </span>
                        </button>
                        <button
                            type="button"
                            className="font-semibold rounded-lg transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed bg-gray-100 text-gray-900 hover:bg-gray-200 focus:ring-gray-200 px-4 py-4 text-base w-auto flex flex-col items-center justify-center p-6 min-h-[150px] text-lg font-semibold shadow-md hover:shadow-lg transition-all duration-200"
                            onClick={() => navigate('/reports/general-ledger-trial-balance')}
                        >
                            <Scale size={48} className="mb-3 text-green-500" aria-hidden="true" />
                            General Ledger Trial Balance
                            <span className="text-sm font-normal text-gray-600 mt-2">
                                Account balances as of the selected date or period.
                            </span>
                        </button>
                        <button
                            type="button"
                            className="font-semibold rounded-lg transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed bg-gray-100 text-gray-900 hover:bg-gray-200 focus:ring-gray-200 px-4 py-4 text-base w-auto flex flex-col items-center justify-center p-6 min-h-[150px] text-lg font-semibold shadow-md hover:shadow-lg transition-all duration-200"
                            onClick={() => navigate('/reports/balance-sheet-report')}
                        >
                            <Scale size={48} className="mb-3 text-indigo-500" aria-hidden="true" />
                            Balance Sheet
                            <span className="text-sm font-normal text-gray-600 mt-2">
                                Snapshot of company assets, liabilities, and equity for a given period.
                            </span>
                        </button>
                        <button
                            type="button"
                            className="font-semibold rounded-lg transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed bg-gray-100 text-gray-900 hover:bg-gray-200 focus:ring-gray-200 px-4 py-4 text-base w-auto flex flex-col items-center justify-center p-6 min-h-[150px] text-lg font-semibold shadow-md hover:shadow-lg transition-all duration-200"
                            onClick={() => navigate('/reports/customer-list')}
                        >
                            <Users size={48} className="mb-3 text-teal-500" aria-hidden="true" />
                            Customer List
                            <span className="text-sm font-normal text-gray-600 mt-2">
                                All registered customers and their credit information.
                            </span>
                        </button>
                        <button
                            type="button"
                            className="font-semibold rounded-lg transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed bg-gray-100 text-gray-900 hover:bg-gray-200 focus:ring-gray-200 px-4 py-4 text-base w-auto flex flex-col items-center justify-center p-6 min-h-[150px] text-lg font-semibold shadow-md hover:shadow-lg transition-all duration-200"
                            onClick={() => navigate('/reports/stock-history')}
                        >
                            <Package size={48} className="mb-3 text-indigo-500" aria-hidden="true" />
                            Stock History Report
                            <span className="text-sm font-normal text-gray-600 mt-2">
                                Detailed history of stock movements, additions, and deductions for items over the selected period.
                            </span>
                        </button>
                        <button
                            type="button"
                            className="font-semibold rounded-lg transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed bg-gray-100 text-gray-900 hover:bg-gray-200 focus:ring-gray-200 px-4 py-4 text-base w-auto flex flex-col items-center justify-center p-6 min-h-[150px] text-lg font-semibold shadow-md hover:shadow-lg transition-all duration-200"
                            onClick={() => navigate('/reports/new-stock-history')}
                        >
                            <Package size={48} className="mb-3 text-indigo-500" aria-hidden="true" />
                            Stock History Report (Version-2)
                            <span className="text-sm font-normal text-gray-600 mt-2">
                                Detailed history of stock movements, additions, and deductions for items over the selected period.
                            </span>
                        </button>
                        <button
                            type="button"
                            className="font-semibold rounded-lg transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed bg-gray-100 text-gray-900 hover:bg-gray-200 focus:ring-gray-200 px-4 py-4 text-base w-auto flex flex-col items-center justify-center p-6 min-h-[150px] text-lg font-semibold shadow-md hover:shadow-lg transition-all duration-200"
                            onClick={() => navigate('/reports/inventory-closing-report')}
                        >
                            <PackageCheck size={48} className="mb-3 text-indigo-600" aria-hidden="true" />
                            Inventory Closing
                            <span className="text-sm font-normal text-gray-600 mt-2 text-center">
                                Final stock levels, valuation at period end, and variance analysis.
                            </span>
                        </button>
                        <button
                            type="button"
                            className="font-semibold rounded-lg transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed bg-gray-100 text-gray-900 hover:bg-gray-200 focus:ring-gray-200 px-4 py-4 text-base w-auto flex flex-col items-center justify-center p-6 min-h-[150px] text-lg font-semibold shadow-md hover:shadow-lg transition-all duration-200"
                            onClick={() => navigate('/reports/profit-and-loss')}
                        >
                            <BarChart size={48} className="mb-3 text-red-500" aria-hidden="true" />
                            Profit & Loss Report
                            <span className="text-sm font-normal text-gray-600 mt-2">
                                Summary of revenues, expenses, and net income for the selected period.
                            </span>
                        </button>
                        <button
                            type="button"
                            className="font-semibold rounded-lg transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed bg-gray-100 text-gray-900 hover:bg-gray-200 focus:ring-gray-200 px-4 py-4 text-base w-auto flex flex-col items-center justify-center p-6 min-h-[150px] text-lg font-semibold shadow-md hover:shadow-lg transition-all duration-200"
                            onClick={() => navigate('/reports/eod-sales-report')}
                        >
                            <FileText size={48} className="mb-3 text-emerald-500" aria-hidden="true" />
                            End of Day Sales Report
                            <span className="text-sm font-normal text-gray-600 mt-2 text-center">
                                Daily summary of cash receipts and credit invoices to track revenue and pending payments.
                            </span>
                        </button>
                        <button
                            type="button"
                            className="font-semibold rounded-lg transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed bg-gray-100 text-gray-900 hover:bg-gray-200 focus:ring-gray-200 px-4 py-4 text-base w-auto flex flex-col items-center justify-center p-6 min-h-[150px] text-lg font-semibold shadow-md hover:shadow-lg transition-all duration-200"
                            onClick={() => navigate('/reports/sales-report')}
                        >
                            <ChartColumnBig size={48} className="mb-3 text-emerald-600" aria-hidden="true" />
                            Sales Report
                            <span className="text-sm font-normal text-gray-600 mt-2 text-center">
                                Summary of revenue, items sold, and customer trends over a selected period.
                            </span>
                        </button>
                        <button
                            type="button"
                            className="font-semibold rounded-lg transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed bg-gray-100 text-gray-900 hover:bg-gray-200 focus:ring-gray-200 px-4 py-4 text-base w-auto flex flex-col items-center justify-center p-6 min-h-[150px] text-lg font-semibold shadow-md hover:shadow-lg transition-all duration-200"
                            onClick={() => navigate('/reports/sales-report-v2')}
                        >
                            <ChartColumnBig size={48} className="mb-3 text-emerald-600" aria-hidden="true" />
                            Sales Report (Version-2)
                            <span className="text-sm font-normal text-gray-600 mt-2 text-center">
                                Advanced summary of revenue, items sold, and customer trends over a selected period.
                            </span>
                        </button>
                        <button
                            type="button"
                            className="font-semibold rounded-lg transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed bg-gray-100 text-gray-900 hover:bg-gray-200 focus:ring-indigo-200 px-4 py-4 text-base w-auto flex flex-col items-center justify-center p-6 min-h-[150px] text-lg font-semibold shadow-md hover:shadow-lg transition-all duration-200"
                            onClick={() => navigate('/reports/sales-performance')}
                        >
                            <TrendingUp size={48} className="mb-3 text-indigo-600" aria-hidden="true" />
                            Sales Performance Analysis
                            <span className="text-sm font-normal text-gray-600 mt-2 text-center">
                                Compare two time periods to analyze growth trends, order volume, and revenue patterns.
                            </span>
                        </button>
                        <button
                            type="button"
                            className="font-semibold rounded-lg transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed bg-gray-100 text-gray-900 hover:bg-gray-200 focus:ring-gray-200 px-4 py-4 w-auto flex flex-col items-center justify-center p-6 min-h-[150px] text-lg shadow-md hover:shadow-lg transition-all duration-200"
                            onClick={() => navigate('/reports/inventory-valuation-report')}
                        >
                            <Warehouse size={48} className="mb-3 text-green-600" aria-hidden="true" />
                            Inventory Valuation Report
                            <span className="text-sm font-normal text-gray-600 mt-2 text-center">
                                Current stock value and asset breakdown for each store location.
                            </span>
                        </button>
                        <button
                            type="button"
                            className="font-semibold rounded-lg transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed bg-gray-100 text-gray-900 hover:bg-gray-200 focus:ring-gray-200 px-4 py-4 w-auto flex flex-col items-center justify-center p-6 min-h-[150px] text-lg shadow-md hover:shadow-lg transition-all duration-200"
                            onClick={() => navigate('/reports/goods-receipt-history')}
                        >
                            <FileClock size={48} className="mb-3 text-orange-500" aria-hidden="true" />
                            Goods Receipt History
                            <span className="text-sm font-normal text-gray-600 mt-2 text-center">
                                Track vendor deliveries and received items over a selected date range.
                            </span>
                        </button>
                        <button
                            type="button"
                            className="font-semibold rounded-lg transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed bg-gray-100 text-gray-900 hover:bg-gray-200 focus:ring-gray-200 px-4 py-4 text-base w-auto flex flex-col items-center justify-center p-6 min-h-[150px] text-lg font-semibold shadow-md hover:shadow-lg transition-all duration-200"
                            onClick={() => navigate('/reports/expense-report')}
                        >
                            <Receipt size={48} className="mb-3 text-orange-600" aria-hidden="true" />
                            Expense Report
                            <span className="text-sm font-normal text-gray-600 mt-2 text-center">
                                Track and analyze company spending and operational costs.
                            </span>
                        </button>
                    </div>
                    <div className="mt-10 text-sm text-gray-600 max-w-2xl text-center">
                        <p className="text-xs text-gray-500 mt-4">
                            <em>
                                <strong>Important Note:</strong> These reports are a snapshot of your financial data
                                for the selected period.
                            </em>
                        </p>
                        <p className="mt-2">
                            All transactions are automatically posted to the General Ledger based on their type and properties.
                        </p>
                    </div>
                </div>
            </Card>
        </div>
    );
};

// Reusable Tab Button Component
const TabButton = ({ label, icon, isActive, onClick }) => (
    <button
        className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-t-lg transition-all duration-200
            ${isActive
                ? 'bg-purple-600 text-white shadow-md'
                : 'text-gray-600 hover:bg-gray-200 hover:text-purple-600'
            }`}
        onClick={onClick}
    >
        {icon} {label}
    </button>
);

export default AccountingReports;